// FT-108 (REQ-03, REQ-04, REQ-07): конфиг панелей формы редактирования брони.
// Вынесен из BookingCard по прецеденту addonsMath.js: детерминированная логика
// маппинга «панель ↔ существующие edit-флаги» тестируется напрямую, без mount
// 12.8k-строчного компонента. Панель — презентационная обёртка (CON-01):
// единственный источник истины — сами boolean-флаги компонента, модуль лишь
// читает/патчит их через ctx (инстанс BookingCard или mock в тестах).

// 7 секций строго по таблице REQ-03 feature.md. snapshotFields — канон из
// таблицы «Снапшот-поля секций» implementation-plan.md (для точки-индикатора
// REQ-04). sideEffects вызываются ТОЛЬКО при открытии (ASM-01, open-only):
// сегодняшний @change тумблера дёргал getTariffs и на выключении — это
// осознанно НЕ переносится.
export const EDIT_SECTIONS = [
  {
    key: "client",
    flags: ["showEditClient"],
    sideEffects: [],
    snapshotFields: ["booking.client_id"],
  },
  {
    key: "car",
    flags: ["editCar"],
    sideEffects: [],
    snapshotFields: ["booking.car_id"],
  },
  {
    key: "dates",
    flags: ["editDate", "editPlace"],
    // Без setDataToEditDate date-picker открылся бы без начальных значений (FM-02)
    sideEffects: ["setDataToEditDate"],
    snapshotFields: ["booking.start_date", "booking.end_date", "booking.location_start", "booking.location_end"],
  },
  {
    key: "tariff",
    flags: ["editTariff", "editSale"],
    // Без getTariffs селект тарифов пуст (FM-02); дублирование списка при
    // повторных открытиях (getTariffs конкатенирует) — паритет с циклами
    // сегодняшнего тумблера, принято в ASM-01.
    sideEffects: ["getTariffs"],
    snapshotFields: [
      "selected_tariff_id",
      "booking.sale",
      "booking.sale_cash",
      "booking.rental_cost_sale",
      "booking.rental_cost_sale_cash",
      "booking_payments_type",
      "booking_payments_period",
      "booking.agent_id",
    ],
  },
  {
    key: "services",
    flags: ["editExtra", "editInsurance", "editEquipment"],
    sideEffects: [],
    snapshotFields: [
      "booking.delivery",
      "booking.delivery_end",
      "booking.equipment",
      "booking.clean_payment",
      "booking.insurance",
      "booking.fine",
      "booking.damage",
      "booking.add_drivers_cost",
      "booking.other",
      "booking.kasko",
      "booking.super_kasko",
      "booking.theft",
      "booking.no_franchise",
      "booking.chair",
      "booking.chair_less_1_year",
      "booking.booster",
      "booking.navigator",
      "booking.wifi_router",
      "booking.mp3",
      "booking.charger",
      "booking.chairs_quantity",
      "booking.boosters_quantity",
      "booking.addons_cost",
    ],
  },
  {
    key: "info",
    flags: ["showDescription"],
    sideEffects: [],
    snapshotFields: ["booking.description", "booking.custom_field_1", "booking.custom_field_2", "booking.custom_field_3"],
  },
  {
    key: "drivers",
    flags: ["showAddDrivers"],
    sideEffects: [],
    // Массив объектов-водителей сравнивается по массиву id (см. driverIds):
    // сами объекты пересоздаются при перезагрузках и по ссылке не сравнимы.
    snapshotFields: ["selected_add_drivers"],
  },
];

// Поле-исключение: снапшотится и сравнивается как массив id, а не примитив.
const DRIVERS_FIELD = "selected_add_drivers";

// Позволяет передавать в функции и объект секции, и её key — computed-прокси
// в BookingCard генерируются по ключам.
function toSection(section) {
  if (typeof section === "string") {
    return EDIT_SECTIONS.find((s) => s.key === section);
  }
  return section;
}

// Двухуровневый доступ по пути 'booking.sale' без eval. ctx.booking может быть
// null в момент снапшота (диалог открывается до догрузки данных) — на
// undefined/null по дороге возвращаем undefined, не бросаем.
export function resolvePath(ctx, path) {
  return String(path)
    .split(".")
    .reduce((value, segment) => (value === null || value === undefined ? undefined : value[segment]), ctx);
}

// REQ-03: панель открыта ⇔ хотя бы один флаг секции true (OR-агрегация).
// Программное включение одного флага (ASM-02: location_* включает editExtra)
// раскрывает панель — паритет сегодняшнего самовключения тумблера.
export function isSectionOpen(section, ctx) {
  return toSection(section).flags.some((flag) => Boolean(ctx[flag]));
}

// Открытие: все флаги секции → true, затем side-effects (ASM-01, open-only).
export function openSection(section, ctx) {
  const resolved = toSection(section);
  resolved.flags.forEach((flag) => {
    ctx[flag] = true;
  });
  resolved.sideEffects.forEach((name) => {
    ctx[name]();
  });
}

// Закрытие: все флаги секции → false. Side-effects НЕ вызываются (ASM-01):
// закрытие панели не должно, например, дублировать список тарифов.
export function closeSection(section, ctx) {
  toSection(section).flags.forEach((flag) => {
    ctx[flag] = false;
  });
}

// DEC-02: все три reset-точки диалога сворачивают ВСЕ панели — диалог всегда
// открывается со свёрнутыми секциями. Side-effects не вызываются: это сброс
// видимости, а не открытие.
export function resetAllSections(ctx) {
  EDIT_SECTIONS.forEach((section) => closeSection(section, ctx));
}

// Ids водителей для снапшота/сравнения. Копия (map создаёт новый массив):
// снапшот не должен мутировать вместе с selected_add_drivers.
function driverIds(list) {
  return (list || []).map((driver) => (driver ? driver.id : driver));
}

// Снапшот значений всех секций при открытии диалога (REQ-04): точка-индикатор
// покажет, какие секции менялись. Примитивы кладутся как есть; водители — как
// массив id.
export function takeSectionSnapshots(ctx) {
  const snapshots = {};
  EDIT_SECTIONS.forEach((section) => {
    const fields = {};
    section.snapshotFields.forEach((path) => {
      fields[path] = path === DRIVERS_FIELD ? driverIds(resolvePath(ctx, path)) : resolvePath(ctx, path);
    });
    snapshots[section.key] = fields;
  });
  return snapshots;
}

// Money-поля реально приходят то строкой ('5' из v-text-field), то числом
// (5 с сервера) — без коэрции индикатор загорался бы от одного клика в поле.
// Решение: сравнивать через Number() ТОЛЬКО когда ОБА значения — число или
// непустая строка, приводимые к конечному числу. '' и null нарочно НЕ
// считаются нулём (иначе очищенное поле «равно» 0 и null — в проекте уже были
// инциденты класса «деньгам доверили пустую строку», см. normalizeMoney в
// addonsMath.js). Всё остальное — строгое ===.
function isNumericScalar(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.trim() !== "" && Number.isFinite(Number(value));
  return false;
}

// Nullable boolean-колонки (kasko, chair и др.): сервер отдаёт null, чекбокс
// пишет false — это одно и то же состояние «не выбрано» (deferred STEP-01 (а)):
// без нормализации точка-индикатор загорался бы от клика вкл/выкл по
// не-заполнявшемуся чекбоксу. Требуем настоящий boolean хотя бы с одной
// стороны: null vs undefined у НЕ-boolean полей (description и т.п.)
// продолжает сравниваться строго.
function isBooleanish(value) {
  return typeof value === "boolean" || value === null || value === undefined;
}

function valuesEqual(a, b) {
  if (isNumericScalar(a) && isNumericScalar(b)) return Number(a) === Number(b);
  if ((typeof a === "boolean" || typeof b === "boolean") && isBooleanish(a) && isBooleanish(b)) {
    return Boolean(a) === Boolean(b);
  }
  return a === b;
}

function idArraysEqual(a, b) {
  const left = a || [];
  const right = b || [];
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
}

// REQ-04: компактное значение секции «Услуги и оборудование» для заголовка
// свёрнутой панели. ~20 полей секции сворачиваются в 3 группы (перечислять
// всё в одну строку нельзя — заголовок обрезается ellipsis'ом на первом же
// длинном значении): Оборудование = легаси-поле equipment + каталожный
// addons_cost (FT-102), Страховка = insurance, Услуги = остальные money-поля.
// Чистая функция: labels (переводы) приходят параметром, ненулевые группы
// склеиваются « · », все нули → «—». Не-числа и пустые строки для ЭТОЙ
// витрины считаются нулём (display-only агрегат, не payload — политика
// «деньгам не верить пустую строку» касается сравнения/отправки, не витрины).
const SERVICES_VALUE_GROUPS = [
  { label: "equipment", fields: ["equipment", "addons_cost"] },
  { label: "insurance", fields: ["insurance"] },
  {
    label: "services",
    fields: ["delivery", "delivery_end", "clean_payment", "add_drivers_cost", "fine", "damage", "other"],
  },
];

export function formatServicesValue(booking, labels) {
  const source = booking || {};
  const names = labels || {};
  const parts = SERVICES_VALUE_GROUPS.map((group) => {
    const total = group.fields.reduce(
      (acc, field) => acc + (isNumericScalar(source[field]) ? Number(source[field]) : 0),
      0
    );
    return { label: names[group.label] || group.label, total };
  }).filter((part) => part.total !== 0);
  if (parts.length === 0) return "—";
  return parts.map((part) => `${part.label}: ${part.total}`).join(" · ");
}

// Shallow-сравнение текущих значений секции со снапшотом (REQ-04).
// Отсутствующий снапшот → false (не dirty): индикатору нечего сравнивать —
// честнее молчать, чем светиться на каждой секции до снятия снапшота.
export function isSectionDirty(section, snapshots, ctx) {
  const resolved = toSection(section);
  const snapshot = snapshots && snapshots[resolved.key];
  if (!snapshot) return false;
  return resolved.snapshotFields.some((path) => {
    if (path === DRIVERS_FIELD) {
      return !idArraysEqual(snapshot[path], driverIds(resolvePath(ctx, path)));
    }
    return !valuesEqual(snapshot[path], resolvePath(ctx, path));
  });
}
